import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/dashboard/widgets/shared/StatusBadge";
import { CustomerInvoicesSection } from "@/components/customers/CustomerInvoicesSection";
import SectionCard from "@/components/customers/SectionCard";
import { useCustomerBudgetedHours, useCustomerHours } from "@/hooks/use-customer-hours";
import { computeCustomerKpis, customerProjectRows, outstandingInvoices, type CustomerProjectRow } from "@/lib/customer-kpi-calc";
import { formatDate, formatNOK, formatPct } from "@/lib/format";
import { TONE_TEXT, type StatusTone } from "@/lib/widget-status";
import type { DBInvoice, DBProject } from "@/lib/db-types";

const DASH = "–";

const OPEN_STATUS_LABELS: Record<string, string> = {
  active: "Pågår",
  kommende: "Kommende",
};

function DeliveredCell({ row }: { row: CustomerProjectRow }) {
  if (row.deliveredAt) return <>{formatDate(row.deliveredAt)}</>;
  return <StatusBadge tone="near" label={OPEN_STATUS_LABELS[row.status] ?? row.status} />;
}

/** Timeavvik som i prosjektlista (VarianceBadge): grønn ≤ 0 %, oransje under 8 %, rød fra 8 % overforbruk. */
function hoursDeviationTone(pct: number): StatusTone {
  if (pct <= 0) return "reached";
  return pct < 8 ? "near" : "miss";
}

function DeviationCell({ pct }: { pct: number | null }) {
  if (pct == null) return <>{DASH}</>;
  const tone = hoursDeviationTone(Math.round(pct));
  return <span className={TONE_TEXT[tone]}>{pct > 0 ? "+" : ""}{formatPct(pct, 0)}</span>;
}

interface CustomerProjectsSectionProps {
  customerId: string;
  projects: DBProject[];
  invoices: DBInvoice[];
}

const TH = "py-2 px-2 font-medium text-muted-foreground";

/**
 * «Det vi har laget»: kundens prosjekter med levert-dato, kontrakt, GEEK reell,
 * reell timepris og budsjettavvik i timer per prosjekt, og en bunnlinje med
 * utestående fakturaer. Formlene er de samme som nøkkeltallene
 * (customer-kpi-calc.ts). Fakturalisten ligger bak «Fakturaer og logg».
 */
export default function CustomerProjectsSection({ customerId, projects, invoices }: CustomerProjectsSectionProps) {
  const projectIds = projects.map((p) => p.id);
  const { data: hours, isLoading: hoursLoading } = useCustomerHours(customerId, projectIds);
  const { data: budgeted, isLoading: budgetedLoading } = useCustomerBudgetedHours(customerId, projectIds);
  const isLoading = hoursLoading || budgetedLoading;
  const [showInvoices, setShowInvoices] = useState(false);

  const kpiInput = projects.map((p) => ({ ...p, is_internal: p.is_internal, project_status: p.project_status ?? "", completed_at: p.completed_at }));
  const rows = customerProjectRows(kpiInput, hours ?? new Map(), budgeted ?? new Map());
  const kpis = computeCustomerKpis(kpiInput, hours ?? new Map());
  const outstanding = outstandingInvoices(invoices);

  return (
    <SectionCard
      title="Det vi har laget"
      meta={rows.length > 0
        ? `${rows.length} ${rows.length === 1 ? "prosjekt" : "prosjekter"}${kpis.valueSold != null ? ` · ${formatNOK(kpis.valueSold)}` : ""}${kpis.bfActual != null ? ` · GEEK reell ${formatNOK(kpis.bfActual)}` : ""}`
        : undefined}
      empty="Ingen prosjekter ennå. De opprettes under Prosjekter, eller når et akseptert tilbud konverteres."
    >
      {isLoading ? (
        <Skeleton className="m-5 h-24" />
      ) : rows.length === 0 && invoices.length === 0 ? null : (
        <div className="space-y-3 px-5 py-4">
          {/* VIB-3030: en kunde kan være fakturert på andres prosjekter uten egne prosjekter. */}
          {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className={`text-left ${TH}`}>Prosjekt</th>
                  <th className={`text-left ${TH}`}>Levert</th>
                  <th className={`text-right ${TH}`}>Kontrakt</th>
                  <th className={`text-right ${TH}`}>GEEK reell</th>
                  <th className={`text-right ${TH}`}>Timepris reell</th>
                  <th className={`text-right ${TH}`}>Budsjettavvik</th>
                  <th className="w-16" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-border/50 last:border-0 hover:bg-muted/30">
                    <td className="py-2 px-2">
                      <span className="font-medium">{r.name}</span>
                      {r.projectCode && <span className="ml-2 text-xs text-muted-foreground tabular-nums">{r.projectCode}</span>}
                    </td>
                    <td className="py-2 px-2 text-muted-foreground"><DeliveredCell row={r} /></td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.contractKr != null ? formatNOK(r.contractKr) : DASH}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.bfActualKr != null ? formatNOK(r.bfActualKr) : DASH}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{r.realHourlyRate != null ? formatNOK(Math.round(r.realHourlyRate)) : DASH}</td>
                    <td className="py-2 px-2 text-right tabular-nums"><DeviationCell pct={r.deviationPct} /></td>
                    <td className="py-2 px-2 text-right">
                      <Link to={`/prosjekter/${r.id}`} className="text-primary hover:underline" aria-label={`Åpne ${r.name}`}>
                        Åpne
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3 text-sm">
            <span className={outstanding.count > 0 ? TONE_TEXT.near : "text-muted-foreground"}>
              {outstanding.count > 0
                ? `Utestående ${formatNOK(outstanding.totalKr)}${outstanding.earliestDueDate ? `, forfall ${formatDate(outstanding.earliestDueDate)}` : ""}`
                : "Ingen utestående fakturaer"}
            </span>
            <Button variant="link" size="sm" className="h-auto p-0" onClick={() => setShowInvoices((v) => !v)} aria-expanded={showInvoices}>
              {showInvoices ? "Skjul fakturaer" : "Fakturaer og logg"}
            </Button>
          </div>
          {showInvoices && <CustomerInvoicesSection invoices={invoices} />}
        </div>
      )}
    </SectionCard>
  );
}
